import { ColDef } from '@ag-grid-enterprise/all-modules'
import { AgGridColumnType, AgGridExportType, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'

enum WasteSampleGridColumnDefType {
    CUSTOM_CHECKBOX = 'CUSTOM_CHECKBOX',
    CASE_ENTITY = 'CASE_ENTITY',
    SAMPLE_ENTITY = 'SAMPLE_ENTITY',
    ARCHIVE_LOCATION = 'ARCHIVE_LOCATION'
}

type WasteSampleGridColumnDefs = { [key in keyof typeof WasteSampleGridColumnDefType]: ColDef | ExportedColGroupDef }

function buildColumnDefs(): WasteSampleGridColumnDefs {
    return {
        [WasteSampleGridColumnDefType.CUSTOM_CHECKBOX]: { type: AgGridColumnType.CUSTOM_CHECKBOX },
        [WasteSampleGridColumnDefType.CASE_ENTITY]: {
            headerName: i18n.tc('entities.case.case'),
            children: [
                {
                    headerName: i18n.tc('waste.grid.caseStudyId'),
                    field: 'caseStudy.casCasenumber',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 110
                },
                {
                    headerName: i18n.tc('waste.grid.validationDate'),
                    colId: 'caseStudy.casValidationDate',
                    type: AgGridColumnType.DATETIME,
                    exportType: AgGridExportType.DATETIME,
                    flex: 1,
                    minWidth: 130
                },
                {
                    headerName: i18n.tc('waste.grid.pathologist'),
                    colId: 'caseStudy.pathologist.normalizedFullName',
                    field: 'caseStudy.pathologist.fullName',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 130
                }
            ]
        },
        [WasteSampleGridColumnDefType.SAMPLE_ENTITY]: {
            headerName: i18n.tc('entities.sample.sample'),
            children: [
                {
                    headerName: i18n.tc('waste.grid.sampleLabel'),
                    field: 'samLabel',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    useForGeneratingBarcodeInExports: true,
                    flex: 1,
                    minWidth: 135
                },
                {
                    headerName: i18n.tc('waste.grid.sampleType'),
                    field: 'sampleType.description',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 130
                },
                {
                    headerName: i18n.tc('waste.grid.location'),
                    colId: 'topographicCode.description',
                    field: 'topographicCodeDescription',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 120
                },
                {
                    headerName: i18n.tc('waste.grid.wasteTechnician'),
                    colId: 'wasteTechnician.normalizedFullName',
                    field: 'wasteTechnician.fullName',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 130
                },
                {
                    headerName: i18n.tc('waste.grid.disposalDate'),
                    field: 'samDisposalDate',
                    type: AgGridColumnType.DATETIME,
                    exportType: AgGridExportType.DATETIME,
                    flex: 1,
                    minWidth: 140
                },
                {
                    headerName: i18n.tc('waste.grid.disposalTargetResponseDate'),
                    field: 'disposalTargetResponseDate',
                    type: AgGridColumnType.DATE,
                    exportType: AgGridExportType.DATE,
                    flex: 1,
                    minWidth: 140
                },
                {
                    headerName: i18n.tc('waste.grid.sampleStatus'),
                    field: 'status',
                    type: AgGridColumnType.WASTE_SAMPLE_STATUS,
                    exportField: 'status.name',
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 110
                },
                {
                    headerName: i18n.tc('waste.grid.preservationDays'),
                    field: 'samDaysBeforeDisposal',
                    type: AgGridColumnType.NUMBER,
                    exportType: AgGridExportType.INTEGER,
                    hide: true,
                    flex: 1,
                    minWidth: 110
                },
                {
                    headerName: i18n.tc('waste.grid.specialPreserve'),
                    field: 'samSpecialPreserve',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.BOOLEAN,
                    cellRenderer: ({ value }: ICellRendererParams) => value ? i18n.tc('global.yes') : i18n.tc('global.no'),
                    filter: 'agSetColumnFilter',
                    filterParams: {
                        newRowsAction: 'keep',
                        cellRenderer: ({ value }: ICellRendererParams) => {
                            if (value === 'true') {
                                return i18n.tc('global.yes')
                            } else if (value === 'false') {
                                return i18n.tc('global.no')
                            }
                            return value
                        },
                        values: [true, false],
                        debounceMs: 1000
                    },
                    hide: false,
                    flex: 1,
                    minWidth: 110
                },
                {
                    headerName: i18n.tc('registry.btn.consultation'),
                    headerTooltip: i18n.tc('registry.btn.consultation'),
                    headerComponentParams: {
                        template: buildIconHeaderTemplate('far fa-glasses')
                    },
                    field: 'caseStudy.caseOfReference',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.BOOLEAN,
                    cellRenderer: ({ value }: ICellRendererParams) => value ? '<i class="success far fa-check"/>' : '',
                    filter: false,
                    hide: false
                }
            ]
        },
        [WasteSampleGridColumnDefType.ARCHIVE_LOCATION]: {
            headerName: '',
            children: [
                {
                    headerName: i18n.tc('waste.grid.archiveLocation'),
                    colId: 'locationLabel',
                    field: 'archivingLocation',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    flex: 1,
                    minWidth: 240
                }
            ]
        }
    }
}

export function wasteSampleGridToBeDiscardedColumnDefs(): (ColDef | ExportedColGroupDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.CASE_ENTITY,
        columnDefs.SAMPLE_ENTITY
    ]
}
export function wasteSampleGridToBeUnarchivedForDisposalColumnDefs(): (ColDef | ExportedColGroupDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.CASE_ENTITY,
        columnDefs.SAMPLE_ENTITY,
        columnDefs.ARCHIVE_LOCATION
    ]
}
export function wasteSampleGridPreservedColumnDefs(): (ColDef | ExportedColGroupDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.CASE_ENTITY,
        columnDefs.SAMPLE_ENTITY
    ]
}
export function wasteSampleGridDiscardedColumnDefs(): (ColDef | ExportedColGroupDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.CASE_ENTITY,
        columnDefs.SAMPLE_ENTITY
    ]
}
